import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

export default function ComplianceConflicts() {
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [pa, setPa] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [openP, setOpenP] = useState(false);
  const { toast } = useToast();
  const [c, setC] = useState({ party: "", conflict_type: "Personal interest", description: "", mitigation: "" });
  const [p, setP] = useState({ partner_name: "", trade_date: format(new Date(), "yyyy-MM-dd"), instrument: "", side: "BUY", quantity: "", notional: "", approved_by: "", notes: "" });

  const load = async () => {
    const [cr, pr] = await Promise.all([
      supabase.from("compliance_conflicts").select("*").order("declared_at", { ascending: false }),
      supabase.from("compliance_pa_dealing").select("*").order("trade_date", { ascending: false }),
    ]);
    setConflicts(cr.data || []); setPa(pr.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveConflict = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_conflicts").insert({ ...c, created_by: u.user?.id });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else { setOpen(false); load(); }
  };
  const savePa = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_pa_dealing").insert({
      partner_name: p.partner_name, trade_date: p.trade_date, instrument: p.instrument,
      side: p.side, quantity: p.quantity ? Number(p.quantity) : null, notional: p.notional ? Number(p.notional) : null,
      approved_by: p.approved_by, notes: p.notes, created_by: u.user?.id,
    });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else { setOpenP(false); load(); }
  };

  return (
    <Tabs defaultValue="conflicts" className="space-y-4">
      <TabsList><TabsTrigger value="conflicts">Conflicts Register</TabsTrigger><TabsTrigger value="pa">PA Dealing Log</TabsTrigger></TabsList>

      <TabsContent value="conflicts">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Conflicts of Interest (§15)</CardTitle>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm">Declare conflict</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Declare conflict</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Party</Label><Input value={c.party} onChange={(e) => setC({ ...c, party: e.target.value })} /></div>
                  <div><Label>Type</Label>
                    <Select value={c.conflict_type} onValueChange={(v) => setC({ ...c, conflict_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["Personal interest","Related party","Gifts & entertainment","Outside business","Other"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Description</Label><Textarea value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} /></div>
                  <div><Label>Mitigation</Label><Textarea value={c.mitigation} onChange={(e) => setC({ ...c, mitigation: e.target.value })} /></div>
                  <Button onClick={saveConflict} disabled={!c.party || !c.description}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {conflicts.length === 0 ? <p className="text-muted-foreground text-sm">No conflicts declared.</p> :
              <table className="w-full text-sm">
                <thead className="border-b"><tr><th className="text-left p-2">Declared</th><th className="text-left p-2">Party</th><th className="text-left p-2">Type</th><th className="text-left p-2">Description</th><th className="text-left p-2">Mitigation</th></tr></thead>
                <tbody>{conflicts.map((cf) => (
                  <tr key={cf.id} className="border-b">
                    <td className="p-2">{format(new Date(cf.declared_at), "dd/MM/yyyy")}</td>
                    <td className="p-2">{cf.party}</td><td className="p-2">{cf.conflict_type}</td>
                    <td className="p-2">{cf.description}</td><td className="p-2">{cf.mitigation ?? "—"}</td>
                  </tr>
                ))}</tbody>
              </table>}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="pa">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Personal Account Dealing</CardTitle>
            <Dialog open={openP} onOpenChange={setOpenP}>
              <DialogTrigger asChild><Button size="sm">Log trade</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Log PA trade</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Partner name</Label><Input value={p.partner_name} onChange={(e) => setP({ ...p, partner_name: e.target.value })} /></div>
                  <div><Label>Trade date</Label><Input type="date" value={p.trade_date} onChange={(e) => setP({ ...p, trade_date: e.target.value })} /></div>
                  <div><Label>Instrument</Label><Input value={p.instrument} onChange={(e) => setP({ ...p, instrument: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label>Side</Label>
                      <Select value={p.side} onValueChange={(v) => setP({ ...p, side: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Quantity</Label><Input type="number" value={p.quantity} onChange={(e) => setP({ ...p, quantity: e.target.value })} /></div>
                    <div><Label>Notional</Label><Input type="number" value={p.notional} onChange={(e) => setP({ ...p, notional: e.target.value })} /></div>
                  </div>
                  <div><Label>Approved by</Label><Input value={p.approved_by} onChange={(e) => setP({ ...p, approved_by: e.target.value })} /></div>
                  <div><Label>Notes</Label><Textarea value={p.notes} onChange={(e) => setP({ ...p, notes: e.target.value })} /></div>
                  <Button onClick={savePa} disabled={!p.partner_name || !p.instrument}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {pa.length === 0 ? <p className="text-muted-foreground text-sm">No PA trades logged.</p> :
              <table className="w-full text-sm">
                <thead className="border-b"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Partner</th><th className="text-left p-2">Instrument</th><th className="text-left p-2">Side</th><th className="text-left p-2">Qty</th><th className="text-left p-2">Notional</th><th className="text-left p-2">Approved by</th></tr></thead>
                <tbody>{pa.map((tr) => (
                  <tr key={tr.id} className="border-b">
                    <td className="p-2">{format(new Date(tr.trade_date), "dd/MM/yyyy")}</td>
                    <td className="p-2">{tr.partner_name}</td><td className="p-2">{tr.instrument}</td>
                    <td className="p-2">{tr.side}</td><td className="p-2">{tr.quantity ?? "—"}</td>
                    <td className="p-2">{tr.notional ?? "—"}</td><td className="p-2">{tr.approved_by ?? "—"}</td>
                  </tr>
                ))}</tbody>
              </table>}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
